import * as Sentry from '@sentry/react';
import type { ErrorComponentProps } from '@tanstack/react-router';
import {
	createRootRoute,
	ErrorComponent,
	Outlet,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthGate } from '@/domains/authentication/components/AuthGate';
import { CopyInfoTrigger } from '@/domains/mission/components/CopyInfoTrigger';
import { getApiErrorDetails } from '@/lib/apiError';

const RootLayout = () => (
	<div className="grid min-h-screen grid-rows-[auto_auto_1fr_auto]">
		<AuthGate>
			<Outlet />
		</AuthGate>
		<TanStackRouterDevtools />
	</div>
);

const RootErrorComponent = ({ error }: ErrorComponentProps) => {
	const { t } = useTranslation();
	const { requestId, codeName } = getApiErrorDetails(error);

	useEffect(() => {
		console.error(error);
		Sentry.captureException(error, {
			tags: { request_id: requestId, connect_code: codeName },
		});
	}, [error, requestId, codeName]);

	return (
		<div className="flex flex-col gap-2 p-4">
			<ErrorComponent error={error} />
			{requestId && (
				<p className="flex items-center gap-1 typography-text-s text-neutral-gray-800">
					{t('common.api.referenceLabel')}:{' '}
					<span className="select-all">{requestId}</span>
					<CopyInfoTrigger value={requestId} size="sm" />
				</p>
			)}
		</div>
	);
};

export const Route = createRootRoute({
	component: RootLayout,
	errorComponent: RootErrorComponent,
});
